import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Alert, Box, Breadcrumbs, Stack, Typography } from '@mui/material';
import React from 'react';
import { Outlet, useLocation, useParams } from 'react-router';
import DashboardContainer from '~/components/Commons/DashboardContainer';
import DianomiSpinner from '~/components/Commons/DianomiSpinner/DianomiSpinner';
import CustomLink from '~/components/Commons/Link/Link';
import PageHeader from '~/components/Commons/PageHeader';
import PageHeaderTitle from '~/components/Typography/PageHeaderTitle';
import useClientPlQuery from '~/hooks/queries/useClientPlQuery';
import useSessionPlQuery from '~/hooks/queries/useSessionPlQuery';
import { useStaffStatsAccessQuery } from '~/hooks/queries/useStaffStatsAccessQuery';
import { getAnalyticsReportingMode } from '~/utils/analyticsReportingMode';
import { getParentChildReportingModeIncompatibilityWarning } from '~/views/Staff/StatLimits/analyticsAccessReportingWarning';
import { StaffAnalyticsConfigModeProvider } from '~/views/Staff/StatLimits/StaffAnalyticsConfigModeContext';
import { StatAccessModeProvider } from '~/views/Staff/StatLimits/StatAccessModeContext';
import ParentAnalyticsAccessSubNav from './ParentAnalyticsAccessSubNav';

function sectionLabelFromPath(pathname: string): string {
  if (pathname.includes('/report-templates')) return 'Report templates';
  return 'Column access';
}

export default function ParentAnalyticsAccessShell() {
  const { accountId, childClientId } = useParams<{
    accountId: string;
    childClientId: string;
  }>();
  const { pathname } = useLocation();
  const sectionLabel = sectionLabelFromPath(pathname);
  const { data: clientData } = useClientPlQuery();
  const { data: sessionData } = useSessionPlQuery();
  const parentMode = getAnalyticsReportingMode(sessionData);
  const childAccessQuery = useStaffStatsAccessQuery('client', childClientId ?? '', {
    parentAccountId: accountId,
  });
  const childPersistedMode = childAccessQuery.data?.analyticsReportingMode ?? null;

  const child = childClientId
    ? Object.values(clientData?.children ?? {}).find(
        (entry) => String(entry.id) === String(childClientId),
      )
    : undefined;
  const childLabel = child?.name?.trim() || `Account ${childClientId ?? ''}`;
  const hubPath = `/client/${accountId ?? ''}/account/child-accounts`;

  const incompatibility = getParentChildReportingModeIncompatibilityWarning(
    parentMode,
    childPersistedMode,
  );

  // While child mode is unknown, still show chrome; gate only the outlet body.
  // Search sync (live strip / Classic Genie parent-universe seed) runs in the route loader.
  const gateOutlet = childAccessQuery.isLoading && !childAccessQuery.data;
  const accessFailed = childAccessQuery.isError && !childAccessQuery.data;

  let outletContent: React.ReactNode;
  if (accessFailed) {
    outletContent = <Box sx={{ p: 2 }}>Error loading analytics access. Please try again.</Box>;
  } else if (gateOutlet) {
    outletContent = <DianomiSpinner loading />;
  } else {
    outletContent = <Outlet />;
  }

  return (
    <StatAccessModeProvider mode="parent">
      <StaffAnalyticsConfigModeProvider persistedMode={childPersistedMode}>
        <DashboardContainer>
          <PageHeader sticky showAllChildrenOnMobile>
            <Stack spacing={0.5}>
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="Breadcrumb"
                sx={{
                  '& .MuiBreadcrumbs-ol': { flexWrap: 'wrap' },
                  '& a': { color: 'text.secondary', textDecoration: 'none' },
                  '& a:hover': { textDecoration: 'underline' },
                }}
              >
                <CustomLink to={hubPath}>Child accounts</CustomLink>
                <Typography color="text.secondary" variant="body2">
                  {childLabel}
                </Typography>
                <Typography color="text.primary" variant="body2" fontWeight={600}>
                  {sectionLabel}
                </Typography>
              </Breadcrumbs>
              <PageHeaderTitle>
                {sectionLabel}: {childLabel}
                {childClientId ? (
                  <Typography
                    component="span"
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{ ml: 1, fontWeight: 400 }}
                  >
                    (ID {childClientId})
                  </Typography>
                ) : null}
              </PageHeaderTitle>
            </Stack>
          </PageHeader>
          <ParentAnalyticsAccessSubNav />
          {incompatibility ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {incompatibility.message}
            </Alert>
          ) : null}
          {outletContent}
        </DashboardContainer>
      </StaffAnalyticsConfigModeProvider>
    </StatAccessModeProvider>
  );
}
